import React, { useEffect, useState } from 'react'
import { RouteComponentProps, Link, useHistory } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { useAuthentication } from '../../hooks/useAuthentication'

import {
  VoucherApproval,
  VoucherApprovalStageStatus,
  VoucherCostCategory,
  VoucherState,
  VoucherType,
} from '../../types'

import {
  Container,
  Grid,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from '@mui/material'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import Alert from '@mui/material/Alert'
import Loader from '../Loader'

import CustomDialog from '../CustomDialog'
import CategoryHelpList from './CategoryHelpList'
import CostCategories from './CostCategories'

import {
  GET_VOUCHER_BY_ID,
  UPDATE_VOUCHER,
  VoucherUpdateInputMap,
  GET_CATEGORIES,
  UpdateVoucherCostCategoryInput,
  UPDATE_VOUCHER_COST_CATEGORY,
  DELETE_VOUCHER_COST_CATEGORY,
  CreateVoucherCostCategoryInput,
  CREATE_VOUCHER_COST_CATEGORY,
  GET_DEPARTMENTS,
  GET_USERS_LIST,
} from './gql'
import ApprovalStages from '../Common/ApprovalStages'
import { toast } from 'react-toastify'

type VoucherProps = RouteComponentProps<{ id?: string }>

const Edit: React.FunctionComponent<VoucherProps> = ({ match }) => {
  const { authState } = useAuthentication()
  const { loading, error, data } = useQuery(GET_VOUCHER_BY_ID, {
    variables: { id: match.params.id },
  })
  const [updateVoucher] = useMutation(UPDATE_VOUCHER)
  const [createVoucherCostCategory] = useMutation(CREATE_VOUCHER_COST_CATEGORY)
  const [updateVoucherCostCategory] = useMutation(UPDATE_VOUCHER_COST_CATEGORY)
  const [deleteVoucherCostCategory] = useMutation(DELETE_VOUCHER_COST_CATEGORY)
  const departmentsResult = useQuery(GET_DEPARTMENTS)
  const usersResult = useQuery(GET_USERS_LIST)

  const categoriesResult = useQuery(GET_CATEGORIES)
  const today = new Date()
  const history = useHistory()
  const [voucher, setVoucher] = useState<VoucherType>({
    id: match.params.id || '',
    description: '',
    department: {
      id: '',
      name: '',
    },
    jamat: '',
    state: 'DRAFT',
    created_at: Date.now(),
    total_amount: 0,
    international_payment: false,
    currency: 'SEK',
    receivers_account_number: '',
    reference_number: 0,
    expense_period: null,
    paid_by_amin: null,
    claimed_by: { id: '', first_name: '', last_name: '', email: '' },
    pay_to: '',
    created_by: {
      id: '',
      first_name: '',
      last_name: '',
      email: '',
      majlis: '',
    },
    financial_year: today.getFullYear(),
    payment_reference: '',
    voucher_cost_category: [] as VoucherCostCategory[],
    voucher_approvals: [] as VoucherApproval[],
    workflow: null,
    rejection_reason: '',
  } as unknown as VoucherType)
  const [categories, setCategories] = useState<Array<any>>([])
  const [users, setUsers] = React.useState<Array<any>>([])
  const [departments, setDepartments] = React.useState<Array<any>>([])
  const [selectedDepartment, setSelectedDepartment] = React.useState('')
  const [helpOpen, setHelpOpen] = useState(false)
  const [showSubmitError, setShowSubmitError] = useState(false)

  const [payToType, setPayToType] = useState<'AMLA' | 'NON_AMLA' | ''>('')

  useEffect(() => {
    if (voucher.pay_to && users.length > 0 && !payToType) {
      const isAmla = users.some(
        (u) => `${u.first_name} ${u.last_name}` === voucher.pay_to
      )
      setPayToType(isAmla ? 'AMLA' : 'NON_AMLA')
    }
  }, [voucher.pay_to, users, payToType])

  const isGenSec = (email?: string | null) =>
    !!email && /^gen\.sec_[^@]+@ahmadiyya\.se$/i.test(email)

  const allowedToEdit =
    authState.authUser.role === 'NATIONAL_SEC_MALL' ||
    authState.authUser.role === 'LOCAL_SEC_MALL' ||
    authState.authUser.role === 'LOCAL_PRESIDENT' ||
    (voucher.created_by?.id === authState.authUser.id &&
      (voucher.state === 'DRAFT' ||
        (voucher.state === 'SUBMITTED' &&
          voucher.voucher_approvals.every(
            (va: any) => va.status === VoucherApprovalStageStatus.PENDING
          ))))

  useEffect(() => {
    if (loading === false && data) {
      setVoucher(data.voucher)
      if (data.voucher.department) {
        setSelectedDepartment(data.voucher.department.id)
      }
    }
  }, [loading, data])

  useEffect(() => {
    if (categoriesResult.loading === false && categoriesResult.data) {
      setCategories(categoriesResult.data.costCategories)
    }
  }, [categoriesResult.loading, categoriesResult.data])

  useEffect(() => {
    if (departmentsResult.loading === false && departmentsResult.data) {
      setDepartments(departmentsResult.data.departments)
    }
  }, [departmentsResult.loading, departmentsResult.data])

  useEffect(() => {
    if (usersResult.loading === false && usersResult.data) {
      setUsers(usersResult.data.users)
    }
  }, [usersResult.loading, usersResult.data])

  if (loading) return <Loader />
  if (error) return <p>ERROR: ${error?.message}</p>

  // Cost Category Callback Functions for CostCategories component
  const handleAddCostCategoryCallback = async (costCategory: any) => {
    console.log('handleAddCostCategory called with:', costCategory)
    try {
      const response = await createVoucherCostCategory({
        variables: {
          input: {
            voucher_id: voucher.id,
            cost_category_id: costCategory.cost_category.id,
            cost_share: costCategory.cost_share,
          },
        },
        context: { headers: { authorization: authState.authToken } },
      })

      if (response.data?.createVoucherCostCategory) {
        const newCostCategory = {
          id: response.data.createVoucherCostCategory.id,
          cost_category: costCategory.cost_category,
          cost_share: costCategory.cost_share,
          receipts: [],
        }

        setVoucher((prev) => ({
          ...prev,
          voucher_cost_category: [
            ...prev.voucher_cost_category,
            newCostCategory,
          ],
        }))
      }
    } catch (error) {
      console.error('Error adding cost category:', error)
      throw error
    }
  }

  const handleUpdateCostCategoryCallback = async (id: string, updates: any) => {
    console.log(
      'handleUpdateCostCategory called with id:',
      id,
      'updates:',
      updates
    )
    try {
      // Only make API call if we're updating cost_category or cost_share
      if (updates.cost_category || updates.cost_share !== undefined) {
        const response = await updateVoucherCostCategory({
          variables: {
            input: {
              id: id,
              cost_category_id: updates.cost_category?.id,
              cost_share: updates.cost_share,
            },
          },
          context: { headers: { authorization: authState.authToken } },
        })

        if (response.data?.updateVoucherCostCategory) {
          setVoucher((prev) => ({
            ...prev,
            voucher_cost_category: prev.voucher_cost_category.map((cat) =>
              cat.id?.toString() === id
                ? {
                  ...cat,
                  cost_category: updates.cost_category || cat.cost_category,
                  cost_share:
                    updates.cost_share !== undefined
                      ? updates.cost_share
                      : cat.cost_share,
                }
                : cat
            ),
          }))
        }
      } else {
        // For receipt updates, just update the local state
        setVoucher((prev) => ({
          ...prev,
          voucher_cost_category: prev.voucher_cost_category.map((cat) =>
            cat.id?.toString() === id ? { ...cat, ...updates } : cat
          ),
        }))
      }
    } catch (error) {
      console.error('Error updating cost category:', error)
      throw error
    }
  }

  const handleDeleteCostCategoryCallback = async (id: string) => {
    try {
      const response = await deleteVoucherCostCategory({
        variables: { id },
        context: { headers: { authorization: authState.authToken } },
      })

      if (response.data?.deleteVoucherCostCategory) {
        setVoucher((prev) => ({
          ...prev,
          voucher_cost_category: prev.voucher_cost_category.filter(
            (cat) => cat.id?.toString() !== id
          ),
        }))
      }
    } catch (error) {
      console.error('Error deleting cost category:', error)
      throw error
    }
  }

  // Helper to check if submission is allowed
  const getSubmitError = () => {
    if (voucher.state !== 'DRAFT') return ''
    if (!voucher.description || voucher.description.trim() === '') {
      return 'Description is required before submitting.'
    }
    if (!voucher.pay_to || voucher.pay_to.trim() === '') {
      return 'Pay To is required before submitting.'
    }
    if (!voucher.voucher_cost_category.length) {
      return 'You must add at least one cost category before submitting.'
    }
    const subCategoryIds = voucher.voucher_cost_category
      .map((vcc) => vcc.cost_category?.id)
      .filter(Boolean)
    if (new Set(subCategoryIds).size !== subCategoryIds.length) {
      return 'Each cost category (Parent and Sub-category) can only be used once.'
    }
    if (
      voucher.voucher_cost_category.some(
        (c) =>
          (typeof c.cost_share === 'string'
            ? parseFloat(c.cost_share) || 0
            : c.cost_share || 0) <= 0
      )
    ) {
      return 'The amount for each cost category must be greater than 0.'
    }
    return ''
  }

  const submitError = getSubmitError()

  const handleSubmit = async (status: 'DRAFT' | 'SUBMITTED') => {
    if (status === 'SUBMITTED' && submitError) {
      setShowSubmitError(true)
      return
    }
    setShowSubmitError(false)

    if (status === 'SUBMITTED') {
      const hasAnyReceipts = voucher.voucher_cost_category.some(
        (vcc) => vcc.receipts?.length
      )

      if (!hasAnyReceipts) {
        if (
          !window.confirm(
            'No receipts have been attached to one or more cost categories. Are you sure you want to submit?'
          )
        ) {
          return
        }
      }
    }

    try {
      const voucherInput = VoucherUpdateInputMap(voucher)
      voucherInput.id = voucher.id
      voucherInput.state = status
      if (selectedDepartment) {
        voucherInput.department = { connect: { id: selectedDepartment } }
      }

      if (voucher.pay_to) {
        voucherInput.pay_to = voucher.pay_to
      }
      const { data } = await updateVoucher({
        variables: { input: voucherInput },
      })
      if (data) {
        toast.success(
          `Voucher ${voucher.reference_number} ${data.state === 'SUBMITTED' ? 'submitted' : 'updated'
          } successfully`
        )
        history.push('/panel')
      }
    } catch (err: any) {
      console.error(err)
      toast.error(
        err.message || `Voucher ${voucher.reference_number} updated failed`
      )
    }
  }

  return (
    <Paper square elevation={0}>
      <Toolbar>
        <Typography variant="h6" component="div">
          {voucher.state === 'DRAFT' ? 'Edit Voucher ' : 'Voucher # '}
          {voucher.reference_number}
          {/* // make icon green if paid by admin */}
        </Typography>
        {voucher.paid_by_amin ? (
          <Box bgcolor="green" m={1} p={1} borderRadius={5}>
            Paid
          </Box>
        ) : (
          ''
        )}
        <Box flexGrow="1" />
        <Button
          size="small"
          aria-label="Back"
          startIcon={<ArrowBackIcon />}
          component={Link}
          to="/panel"
        >
          Back
        </Button>
      </Toolbar>
      <Container>
        {/* Description Row - Full Width */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              size="small"
              label="Description"
              value={voucher.description}
              variant="outlined"
              onChange={(e: any) =>
                setVoucher({ ...voucher, description: e.target.value })
              }
              disabled={!allowedToEdit}
              error={
                showSubmitError &&
                (!voucher.description || voucher.description.trim() === '')
              }
            />
          </Grid>
        </Grid>
        {/* First Row of Fields */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Reference Number"
              value={voucher.reference_number}
              variant="outlined"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="State"
              value={voucher.state}
              variant="outlined"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Created By"
              value={
                voucher.created_by?.first_name +
                ' ' +
                voucher.created_by?.last_name
              }
              variant="outlined"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* Empty space for balance */}
          </Grid>
        </Grid>
        {/* Second Row of Fields */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl
              fullWidth
              size="small"
              error={showSubmitError && !payToType}
            >
              <InputLabel id="payto-type-label">Pay to (Type)</InputLabel>
              <Select
                labelId="payto-type-label"
                id="payto-type-select"
                value={payToType}
                label="Pay to (Type)"
                onChange={(e: any) => {
                  setPayToType(e.target.value)
                  if (e.target.value !== payToType) {
                    setVoucher((prev) => ({ ...prev, pay_to: '' }))
                  }
                }}
                disabled={!allowedToEdit}
              >
                <MenuItem value="AMLA">Amla Member</MenuItem>
                <MenuItem value="NON_AMLA">Member or Vendor</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {payToType === 'AMLA' ? (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  id="payto-select"
                  options={users.filter(
                    (u) =>
                      !authState.authUser.majlis ||
                      u.majlis === authState.authUser.majlis ||
                      `${u.first_name} ${u.last_name}` === voucher.pay_to
                  )}
                  getOptionLabel={(option) =>
                    `${option.first_name} ${option.last_name}`
                  }
                  value={users.find((u) => `${u.first_name} ${u.last_name}` === voucher.pay_to) || null}
                  onChange={(event: any, newValue: any | null) => {
                    if (newValue) {
                      setVoucher((prev) => ({
                        ...prev,
                        pay_to: `${newValue.first_name} ${newValue.last_name}`
                      }))
                    } else {
                      setVoucher((prev) => ({ ...prev, pay_to: '' }))
                    }
                  }}
                  disabled={!allowedToEdit}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Amla member"
                      variant="outlined"
                      size="small"
                      error={
                        showSubmitError &&
                        (!voucher.pay_to || voucher.pay_to.trim() === '')
                      }
                    />
                  )}
                />
              </FormControl>
            </Grid>
          ) : payToType === 'NON_AMLA' ? (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Member or Vendor Name"
                value={voucher.pay_to || ''}
                variant="outlined"
                onChange={(e: any) =>
                  setVoucher({ ...voucher, pay_to: e.target.value })
                }
                disabled={!allowedToEdit}
                error={
                  showSubmitError &&
                  (!voucher.pay_to || voucher.pay_to.trim() === '')
                }
              />
            </Grid>
          ) : (
            <Grid item xs={12} sm={6} md={3} />
          )}

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="voucehr-financial-year-label">
                Financial year
              </InputLabel>
              <Select
                id="voucehr-financial-year"
                value={voucher.financial_year}
                label="Financial year"
                disabled={
                  !allowedToEdit ||
                  authState.authUser.role !== 'NATIONAL_SEC_MALL'
                }
                onChange={(e: any) =>
                  setVoucher({ ...voucher, financial_year: e.target.value })
                }
              >
                {Array(
                  voucher.financial_year + 1 - (voucher.financial_year - 2)
                )
                  .fill('')
                  .map((v, idx) => (
                    <MenuItem
                      key={`financial-year-${idx}`}
                      value={voucher.financial_year + 1 - idx}
                    >{`${voucher.financial_year - idx}-${voucher.financial_year - idx + 1
                      }`}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="department-select-label">Department</InputLabel>
              <Select
                labelId="department-select-label"
                id="department-select"
                value={selectedDepartment}
                label="Department"
                onChange={(e) => setSelectedDepartment(e.target.value)}
                disabled={
                  !allowedToEdit ||
                  !(
                    authState.authUser.role === 'NATIONAL_SEC_MALL' ||
                    authState.authUser.role === 'LOCAL_SEC_MALL' ||
                    authState.authUser.role === 'LOCAL_PRESIDENT' ||
                    isGenSec(authState.authUser.email) ||
                    (authState.authUser.role === 'LOCAL_AMIN' &&
                      voucher.created_by?.id === authState.authUser.id)
                  )
                }
              >
                {departments.map((d: any) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        {/* Third Row of Fields */}
        {voucher?.created_by?.majlis === 'NATIONAL' && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={voucher.international_payment}
                    onChange={(e) => {
                      const checked = e.target.checked
                      setVoucher((prev) => ({
                        ...prev,
                        international_payment: checked,
                        currency: checked ? '' : 'SEK',
                      }))
                    }}
                    disabled={!allowedToEdit}
                  />
                }
                label="International Payment"
              />
            </Grid>
            {voucher.international_payment && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ mt: 2 }}
                  required={voucher.international_payment}
                  error={voucher.international_payment && !voucher.currency}
                >
                  <InputLabel id="currency-select-label">Currency</InputLabel>
                  <Select
                    labelId="currency-select-label"
                    id="currency-select"
                    value={voucher.currency || ''}
                    label="Currency"
                    onChange={(e) =>
                      setVoucher((prev) => ({
                        ...prev,
                        currency: e.target.value,
                      }))
                    }
                    disabled={
                      !allowedToEdit || (!voucher.international_payment && true)
                    }
                  >
                    {(voucher.international_payment
                      ? ['CNH', 'DKK', 'EUR', 'GBP', 'NOK', 'USD']
                      : ['SEK']
                    ).map((curr) => (
                      <MenuItem key={curr} value={curr}>
                        {curr}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        )}
        <ApprovalStages voucher={voucher} />
        {voucher.state === 'REJECTED' && (
          <Box
            bgcolor="#f4c6af"
            color="#5e321b"
            p={1}
            borderRadius={2}
            sx={{ mb: 2 }}
          >
            {voucher.rejection_reason}
          </Box>
        )}
        <Toolbar variant="dense" disableGutters>
          <Typography variant="h6" component="div" mt={2}>
            Categories
            <IconButton
              size="small"
              onClick={() => setHelpOpen(true)}
              aria-label="Show category help"
              sx={{ ml: 1 }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Typography>
        </Toolbar>
        <CustomDialog
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          title="Category List"
        >
          <CategoryHelpList />
        </CustomDialog>
        <CostCategories
          voucher={voucher}
          readonly={
            !allowedToEdit || voucher.state === 'PAID' || !voucher.workflow
          }
          onAdd={handleAddCostCategoryCallback}
          onUpdate={handleUpdateCostCategoryCallback}
          onDelete={handleDeleteCostCategoryCallback}
        />
        {voucher.state === 'DRAFT' && showSubmitError && submitError && (
          <Box mb={2}>
            <Alert severity="error">{submitError}</Alert>
          </Box>
        )}
        {voucher.state === 'DRAFT' && voucher.workflow && (
          <Box display="flex" justifyContent="flex-end" py={2}>
            <Box marginRight={2}>
              <Button
                variant="contained"
                color="inherit"
                onClick={() => handleSubmit('DRAFT')}
              >
                Save Draft
              </Button>
            </Box>
            <Box marginRight={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSubmit('SUBMITTED')}
              >
                Submit
              </Button>
            </Box>
          </Box>
        )}
        {voucher.state === VoucherState.SUBMITTED &&
          allowedToEdit &&
          voucher.workflow && (
            <Box display="flex" justifyContent="flex-end" py={2}>
              <Box marginRight={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleSubmit('SUBMITTED')}
                >
                  Update
                </Button>
              </Box>
            </Box>
          )}
      </Container>
    </Paper>
  )
}

export default Edit
